import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useUser } from '../../contexts/userContext'; 
import { resolveMediaUrl } from '../../api/client';
import miroAvatar from '../../assets/miro-avatar.png';

// Helper function to resolve image URLs, similar to MinimalNavbar.jsx
const getResolvedImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${process.env.REACT_APP_FRONTEND_URL}${url}`;
  return `${process.env.REACT_APP_FRONTEND_URL}/${url}`;
};

const MessageBubble = ({ message, onActionClick }) => {
  const { userDetails } = useUser(); 
  const isUser = message.message_type === 'user';
  const isSystem = message.message_type === 'system';
  const [isDownloading, setIsDownloading] = React.useState(false);
  
  const userProfilePic = isUser ? (userDetails?.user_profile_picture || userDetails?.profile_picture) : null;
  // Decide avatar per message type
let imageUrl = null;

if (isUser && userProfilePic) {
  imageUrl = getResolvedImageUrl(userProfilePic);
} else if (!isUser && !isSystem) {
  // BOT message
  imageUrl = miroAvatar;
};



  const getAvatarColor = () => {
    if (isUser) return '#0ea5e9';
    if (isSystem) return '#0ea5e9';
    return '#3b82f6';
  };

  const handleDownloadClick = async (downloadUrl) => {
    setIsDownloading(true);
    try {
      const absoluteUrl = resolveMediaUrl(downloadUrl);
      const token = localStorage.getItem('token'); // Get the token

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`; // Add Authorization header
      }

      const response = await fetch(absoluteUrl, { headers }); // Pass headers to fetch
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const match = downloadUrl.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
      const fileName = match ? `report_${match[0]}.pdf` : 'report.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      // You can add a user-facing error message here
    } finally {
      setIsDownloading(false);
    }
  };

  const formatContent = (content) => {
    if (!content) return '';
    
    // Split content into lines and format
    return content.split('\n').map((line, index) => {
      if (line.trim() === '') return <br key={index} />;
      
      // Check for bullet points
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        return (
          <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.6, color: 'inherit', lineHeight: 1.6, fontSize: '0.92rem' }}>
            • {line.trim().substring(1).trim()}
          </Typography>
        );
      }
      
      // Check for numbered lists
      if (/^\d+\./.test(line.trim())) {
        return (
          <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.6, color: 'inherit', lineHeight: 1.6, fontSize: '0.92rem' }}>
            {line.trim()}
          </Typography>
        );
      }
      
      return (
        <Typography key={index} variant="body2" sx={{ mb: 0.6, color: 'inherit', lineHeight: 1.6, fontSize: '0.92rem' }}>
          {line}
        </Typography>
      );
    });
  };

  const pulseAnimation = {
    initial: { scale: 1, boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)' },
    animate: {
      scale: [1, 1.02, 1],
      boxShadow: [
        '0 4px 15px rgba(99, 102, 241, 0.3)',
        '0 4px 25px rgba(99, 102, 241, 0.5)',
        '0 4px 15px rgba(99, 102, 241, 0.3)'
      ],
    },
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const renderFunctionResults = (functionResults) => {
    if (!functionResults || Object.keys(functionResults).length === 0) return null;

    return (
      <Accordion sx={{ mt: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon color="primary" />
            <Typography variant="subtitle2">
              Function Results ({Object.keys(functionResults).length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {Object.entries(functionResults).map(([functionName, result], index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {functionName.replace(/_/g, ' ').toUpperCase()}
              </Typography>
              {result.error ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {result.error}
                </Alert>
              ) : (
                <Paper sx={{ p: 2, bgcolor: '#f8f9ff', border: '1px solid #e0e0e0' }}>
                  <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </Paper>
              )}
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSkills = (skills) => {
    if (!skills || !Array.isArray(skills) || skills.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          <SchoolIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
          Extracted Skills ({skills.length})
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {skills.slice(0, 10).map((skill, index) => (
            <Chip
              key={index}
              label={skill}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '0.75rem'
              }}
            />
          ))}
          {skills.length > 10 && (
            <Chip
              label={`+${skills.length - 10} more`}
              size="small"
              variant="outlined"
              sx={{ color: '#666' }}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderMatches = (matches) => {
    if (!matches || !Array.isArray(matches) || matches.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          <WorkIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
          Job Matches ({matches.length})
        </Typography>
        <List dense>
          {matches.slice(0, 3).map((match, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <ListItemIcon>
                <WorkIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={match.title || match.job_title}
                secondary={`${match.company || 'Unknown Company'} - ${match.match_percentage || 0}% match`}
              />
            </ListItem>
          ))}
          {matches.length > 3 && (
            <ListItem sx={{ px: 0 }}>
              <ListItemText
                secondary={`+${matches.length - 3} more matches`}
                sx={{ fontStyle: 'italic' }}
              />
            </ListItem>
          )}
        </List>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          maxWidth: '82%',
          flexDirection: isUser ? 'row-reverse' : 'row'
        }}
      >
        {/* Avatar */}
        <Avatar
          src={imageUrl}
          sx={{
            bgcolor: getAvatarColor(),
            width: 38,
            height: 38,
            boxShadow: isUser ? '0 4px 12px rgba(14, 165, 233, 0.35)' : '0 4px 12px rgba(59, 130, 246, 0.25)',
            border: '2px solid white',
          }}
        >
          {!imageUrl && (isUser ? <PersonIcon /> : (isSystem ? <CheckIcon/> : <BotIcon />))}
        </Avatar>

        {/* Message Content */}
        <Paper
          elevation={0}
          sx={{
            p: 2.2,
            borderRadius: isUser ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
            background: isUser 
              ? 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)' 
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: isUser ? '1.5px solid #0ea5e9' : '1px solid rgba(226, 232, 240, 0.9)',
            boxShadow: isUser 
              ? '0 6px 18px rgba(14, 165, 233, 0.15)' 
              : '0 4px 20px rgba(15, 23, 42, 0.05)',
            color: isUser ? '#0f172a' : '#1e293b',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: isUser 
                ? '0 8px 22px rgba(14, 165, 233, 0.22)' 
                : '0 6px 24px rgba(15, 23, 42, 0.08)',
            }
          }}
        >
          {/* Message Content */}
          <Box sx={{ mb: 1 }}>
            {formatContent(message.content)}
          </Box>

          {/* Download Link / Report Action */}
          {(message.download_url || message.action_type === 'download_report') && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  variant="text"
                  size="small"
                  disabled={isDownloading}
                  onClick={() => {
                    const url = message.download_url || `/reports/download/${message.action_data?.job_id}/`;
                    handleDownloadClick(url);
                  }}
                  startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%) !important',
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: 3,
                    py: 0.8,
                    fontSize: '0.8rem',
                    width: 'fit-content',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%) !important',
                      boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
                    },
                    '&.Mui-disabled': {
                      background: '#e2e8f0 !important',
                      color: '#94a3b8 !important',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {isDownloading ? 'Downloading...' : (message.action_data?.button_label || 'Download Report')}
                </Button>
              </motion.div>
            </Box>
          )}

          {/* View Candidates Button / Modal Trigger */}
          {((message.results && message.results.length > 0) || message.action_type === 'modal_trigger') && (
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-start', background: 'none' }}>
              <motion.div
                initial={{ scale: 1 }}
                animate={{
                  scale: [1, 1.02, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                style={{ display: 'inline-block', background: 'none' }}
              >
                <Button
                  variant="text" // Change to text to avoid default contained styles
                  size="small"
                  onClick={() => {
                    if (message.action_type === 'modal_trigger') {
                      onActionClick && onActionClick({ 
                        type: 'VIEW_CANDIDATES', 
                        results: message.action_data?.candidates || [], 
                        summary: message.content 
                      });
                    } else {
                      onActionClick && onActionClick({ 
                        type: 'VIEW_CANDIDATES', 
                        results: message.results, 
                        summary: message.summary || message.content 
                      });
                    }
                  }}
                  startIcon={<PeopleIcon fontSize="small" />}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%) !important',
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '20px',
                    px: 3,
                    py: 0.8,
                    fontSize: '0.8rem',
                    width: 'fit-content',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%) !important',
                      boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
                    },
                    '&.MuiButton-root': {
                      backgroundColor: 'transparent !important',
                    }
                  }}
                >
                  {message.action_data?.button_label || 'View Candidates'}
                </Button>
              </motion.div>
            </div>
          )}

          {/* Error Display */}
          {message.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {message.error}
            </Alert>
          )}

          {/* Function Results */}
          {message.function_results && renderFunctionResults(message.function_results)}

          {/* Skills Display */}
          {message.skills && renderSkills(message.skills)}

          {/* Job Matches */}
          {message.matches && renderMatches(message.matches)}

          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Suggestions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {message.suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    size="small"
                    onClick={() => onActionClick && onActionClick(suggestion)}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Timestamp */}
          {message.timestamp && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                textAlign: isUser ? 'right' : 'left',
                color: isUser ? 'rgba(255, 255, 255, 0.75)' : '#94a3b8',
                fontSize: '0.68rem',
                fontWeight: 500
              }}
            >
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MessageBubble;